import {createSlice, PayloadAction} from '@reduxjs/toolkit'
import {RootState} from "../store/store";
import {authApi} from "../services/authService.ts";
import {customerAuthApi} from "../services/customerAuthService.ts";
import {isTokenExpired} from "../middleware/authPersistMiddleware";

export type UserType = 'barber' | 'customer' | null;

interface TokenPayload {
    token: string;
    refresh_token: string;
}

// Lightweight payload echoed back by POST /api/admin/impersonate[-customer]/{id};
// stored alongside the impersonation JWT so the banner can label the session.
// `kind` was added in BE #55 — both barber and customer endpoints now echo it.
// `barberShopId / barberShopName` are barber-only (always null for customers).
export interface ImpersonationTarget {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
    barberShopId?: string | null;
    barberShopName?: string | null;
    kind?: 'barber' | 'customer';
}

interface AuthState {
    token: string | null;
    refreshToken: string | null;
    customerToken: string | null;
    customerRefreshToken: string | null;
    // Memory-only — never persisted. While set, the API layer uses this JWT
    // in place of `token` so every barber-scoped request runs as the target
    // barber. Dropping it returns the admin to their own session without
    // touching the BE (the impersonation JWT just falls out of scope).
    impersonationToken: string | null;
    impersonationTarget: ImpersonationTarget | null;
}

// sessionStorage key for impersonation. Picked over localStorage so the
// session ends when the tab closes — admins shouldn't accidentally still be
// "in" a target user's account in a new tab the next day.
const IMP_STORAGE_KEY = 'bm-impersonation'

interface PersistedImpersonation {
    token: string
    target: ImpersonationTarget
}

const readImpersonation = (): PersistedImpersonation | null => {
    try {
        const raw = sessionStorage.getItem(IMP_STORAGE_KEY)
        if (!raw) return null
        const parsed = JSON.parse(raw) as PersistedImpersonation
        if (!parsed?.token || !parsed?.target?.id) return null
        return parsed
    } catch {
        return null
    }
}

const writeImpersonation = (data: PersistedImpersonation | null): void => {
    try {
        if (data) sessionStorage.setItem(IMP_STORAGE_KEY, JSON.stringify(data))
        else sessionStorage.removeItem(IMP_STORAGE_KEY)
    } catch {
        // ignore — private mode / quota
    }
}

// Hydrate initial state from localStorage + sessionStorage. The hard redirect
// after starting impersonation wipes Redux; reading the JWT back out of
// sessionStorage on boot is what makes the swap actually take effect.
const hydrateFromStorage = (): AuthState => {
    try {
        const barber = JSON.parse(localStorage.getItem('tokens') as string) as TokenPayload | null
        const customer = JSON.parse(localStorage.getItem('customerTokens') as string) as TokenPayload | null
        const imp = readImpersonation()
        return {
            token: barber?.token ?? null,
            refreshToken: barber?.refresh_token ?? null,
            customerToken: customer?.token ?? null,
            customerRefreshToken: customer?.refresh_token ?? null,
            impersonationToken: imp?.token ?? null,
            impersonationTarget: imp?.target ?? null,
        }
    } catch {
        return {
            token: null, refreshToken: null, customerToken: null, customerRefreshToken: null,
            impersonationToken: null, impersonationTarget: null,
        }
    }
}

const authSlice = createSlice({
    name: 'auth',
    initialState: hydrateFromStorage(),
    reducers: {
        // Used by apiService for token refresh (not from components)
        setTokens: (state, {payload}: PayloadAction<TokenPayload>) => {
            state.token = payload.token
            state.refreshToken = payload.refresh_token
        },
        setCustomerTokens: (state, {payload}: PayloadAction<TokenPayload>) => {
            state.customerToken = payload.token
            state.customerRefreshToken = payload.refresh_token
        },
        // Logout actions — pure state clearing, side effects handled by listener middleware
        logout: () => {
            writeImpersonation(null)
            return {
                token: null, refreshToken: null, customerToken: null, customerRefreshToken: null,
                impersonationToken: null, impersonationTarget: null,
            }
        },
        barberLogout: (state) => {
            state.token = null
            state.refreshToken = null
            state.impersonationToken = null
            state.impersonationTarget = null
            writeImpersonation(null)
        },
        customerLogout: (state) => {
            state.customerToken = null
            state.customerRefreshToken = null
        },
        // Admin impersonation. The token is persisted in sessionStorage so the
        // hard redirect into the target's dashboard (where AdminPlatformLayout
        // is no longer mounted) still picks up the swapped Authorization
        // header on the very first request after reload.
        startImpersonation: (state, {payload}: PayloadAction<{token: string; target: ImpersonationTarget}>) => {
            state.impersonationToken = payload.token
            state.impersonationTarget = payload.target
            writeImpersonation({token: payload.token, target: payload.target})
        },
        stopImpersonation: (state) => {
            state.impersonationToken = null
            state.impersonationTarget = null
            writeImpersonation(null)
        },
    },
    extraReducers: (builder) => {
        // Barber login — single source for token handling
        builder.addMatcher(
            authApi.endpoints.login.matchFulfilled,
            (state, action) => {
                const payload = action.payload as unknown as TokenPayload
                state.token = payload.token
                state.refreshToken = payload.refresh_token
            },
        )
        // Barber invite acceptance (FE #33) — InviteAccept.tsx dispatches
        // setTokens directly after the acceptInvite mutation succeeds,
        // mirroring the login flow without the module cycle that pulling
        // barberInviteService in here would introduce.
        // Customer login
        builder.addMatcher(
            customerAuthApi.endpoints.customerLogin.matchFulfilled,
            (state, {payload}: PayloadAction<TokenPayload>) => {
                state.customerToken = payload.token
                state.customerRefreshToken = payload.refresh_token
            },
        )
        // Customer register (also returns tokens)
        builder.addMatcher(
            customerAuthApi.endpoints.customerRegister.matchFulfilled,
            (state, {payload}: PayloadAction<TokenPayload>) => {
                state.customerToken = payload.token
                state.customerRefreshToken = payload.refresh_token
            },
        )
    },
})

export const {
    logout, barberLogout, setTokens, setCustomerTokens, customerLogout,
    startImpersonation, stopImpersonation,
} = authSlice.actions

// ─── Selectors ──────────────────────────────────────────────────
export const isBarberLoggedIn = (state: RootState): boolean => state.auth.token !== null;
export const selectBarberToken = (state: RootState): string | null => state.auth.token;
export const selectImpersonationToken = (state: RootState): string | null => state.auth.impersonationToken;
export const selectImpersonationTarget = (state: RootState): ImpersonationTarget | null => state.auth.impersonationTarget;
export const isImpersonating = (state: RootState): boolean => state.auth.impersonationToken !== null;

export const isCustomerLoggedIn = (state: RootState): boolean => state.auth.customerToken !== null;
export const selectCustomerToken = (state: RootState): string | null => state.auth.customerToken;

export const isLoggedIn = (state: RootState): boolean =>
    state.auth.token !== null || state.auth.customerToken !== null;

export const isAnyUserLoggedIn = (state: RootState): boolean =>
    state.auth.token !== null || state.auth.customerToken !== null;

export const getUserType = (state: RootState): UserType => {
    if (state.auth.token !== null) return 'barber';
    if (state.auth.customerToken !== null) return 'customer';
    return null;
};

// Token expiry selectors (60s buffer = proactive refresh)
export const isBarberTokenExpired = (state: RootState): boolean => isTokenExpired(state.auth.token, 60);
export const isCustomerTokenExpired = (state: RootState): boolean => isTokenExpired(state.auth.customerToken, 60);

export const selectAuth = (state: RootState) => state.auth;

export default authSlice.reducer
